/**
 * Workspace Overview — Shared Models
 *
 * Single source of truth for DTOs, enums, and inputs consumed by:
 *   - `WorkspaceOverviewService` (HTTP stubs returning mock data)
 *   - `WorkspaceOverviewComponent` (container)
 *   - All 8 widget components under `components/workspace-overview/widgets/`
 *
 * Backend devs: keep the response shapes in sync with `services/workspace-overview.service.ts`
 * once the real endpoints are implemented.
 */

// ─── Enums ──────────────────────────────────────────────────────────────────

/**
 * View scope toggle in the dashboard header.
 *  - `Personal` → only data the current user has access to
 *  - `Admin`    → org-wide aggregated data (admin-only)
 */
export enum WorkspaceViewMode {
    Personal = 'personal',
    Admin = 'admin',
}

/**
 * Period filter toggle in the dashboard header.
 * `Custom` reveals the custom date-range picker.
 */
export enum PeriodFilter {
    Week = 'week',
    Month = 'month',
    Quarter = 'quarter',
    Custom = 'custom',
}

// ─── Common types ───────────────────────────────────────────────────────────

/**
 * Inclusive date range used when `period === Custom`.
 */
export interface DateRange {
    from: Date;
    to: Date;
}

/**
 * Standard set of inputs accepted by every widget. Container passes these
 * down, and each widget reacts via ngOnChanges (or signal effects).
 */
export interface WidgetInputs {
    mode: WorkspaceViewMode;
    period: PeriodFilter;
    range?: DateRange | null;
}

// ─── Widget DTOs ────────────────────────────────────────────────────────────

export interface TimeReclaimedBreakdown {
    threadType: string;
    label: string;
    threadCount: number;
    minutesReclaimed: number;
    hoursReclaimed: number;
}

export interface TimeReclaimedData {
    /** Total hours reclaimed across all AI features. */
    reclaimedHours: number;
    /** Hours of equivalent manual work (denominator in the rainbow visual). */
    manualHours: number;
    /** Dollar value of reclaimed time at the backend-configured hourly rate. */
    costSaved: number;
    breakdown: TimeReclaimedBreakdown[];
    /** Percentage change vs previous month; null when no prior data exists. */
    percentageChangeFromLastMonth: number | null;
}

// ─── 2) Bob Assisted Hours ──────────────────────────────────────────────────

/**
 * Bob surface = one of the entry points where Bob is invoked. The list
 * matches the spec: Ask Bob, Bob - Documents, Bob - Questionnaire,
 * Bob - Compare, Bob - Geotechnical Analysis.
 */
export interface BobSurfaceUsage {
    name: string;
    seconds: number;
    /** Share of the total duration, 0-100. */
    percent: number;
}

export interface BobAssistedHoursData {
    totalSeconds: number;
    surfaces: BobSurfaceUsage[];
}

// ─── 3) Documents Enhanced ──────────────────────────────────────────────────

/**
 * A single bucket cell in the per-feature heatmap row.
 * `date` is an ISO 8601 date (yyyy-mm-dd) representing the day bucket.
 */
export interface DocumentsEnhancedDailyBucket {
    date: string;
    count: number;
}

export interface DocumentsEnhancedFeatureRow {
    /** Display name of the AI feature (Summary, Checklist Items, …). */
    name: string;
    /** Total count over the visible window. */
    total: number;
    /** One entry per day column in the visible window. */
    daily: DocumentsEnhancedDailyBucket[];
}

export interface DocumentsEnhancedData {
    /** Overall count of unique enhanced documents in the window. */
    total: number;
    features: DocumentsEnhancedFeatureRow[];
    /** Day columns (ISO dates) used by the table header. */
    dateColumns: string[];
}

// ─── 4) Ask Bob – Standalone ────────────────────────────────────────────────

export interface AskBobOutputsBreakdown {
    total: number;
    pdf: number;
    docx: number;
    xlsx: number;
    others: number;
}

export interface AskBobModeSplit {
    /** Share of conversations started in Ask mode, 0-100. */
    ask: number;
    /** Share of conversations started in Agentic mode, 0-100. */
    agentic: number;
}

export interface AskBobStandaloneData {
    conversations: number;
    documentsAnalyzed: number;
    outputs: AskBobOutputsBreakdown;
    modeSplit: AskBobModeSplit;
}

// ─── 5) Untapped Potential ──────────────────────────────────────────────────

/**
 * String enum for the action triggered when the user clicks a feature chip.
 *  - `OpenDoc`     → open external documentation URL
 *  - `OpenDialog`  → emit an event the container handles (e.g. open Generate with AI)
 *  - `Navigate`    → router.navigate to an internal route
 */
export enum UntappedActionType {
    OpenDoc = 'open-doc',
    OpenDialog = 'open-dialog',
    Navigate = 'navigate',
}

export interface UntappedFeature {
    /** Internal id used to emit click events. */
    id: string;
    /** Display label shown in the chip. */
    label: string;
    /** Icon (PrimeIcons / Lucide / asset path) — choose per design. */
    icon?: string;
    action: UntappedActionType;
    /** URL or route depending on `action`. Container interprets this. */
    target?: string;
}

export interface UntappedPotentialData {
    /** Empty array means "all features explored" — render completion message. */
    features: UntappedFeature[];
}

// ─── 6) Feedback & Quality (admin-only) ─────────────────────────────────────

export interface FeedbackPerFeature {
    name: string;
    /** Positive sentiment share for the feature, 0-100. */
    positivePct: number;
}

export interface FeedbackQualityData {
    /** Overall positive sentiment, 0-100. */
    positiveSentimentPct: number;
    /** NPS score, -100 to 100. */
    npsScore: number;
    /** Average rating on the 1-5 scale. */
    avgRating: number;
    /** Bob thumbs-up share, 0-100. */
    bobThumbsUpPct: number;
    /** Share of prompts dismissed without responding, 0-100. */
    dismissRatePct: number;
    perFeature: FeedbackPerFeature[];
}

// ─── 7) Projects ────────────────────────────────────────────────────────────

/**
 * Counts of projects by status, used by the bar chart.
 * Status names mirror the existing PROJECT_USER_ROLE / status taxonomy.
 */
export interface ProjectStatusBucket {
    name: string;
    count: number;
}

export interface ProjectsData {
    statuses: ProjectStatusBucket[];
}

// ─── 8) Document Counts ─────────────────────────────────────────────────────

export interface DocumentCountsData {
    overdue: number;
    pendingApprovals: number;
    pendingSignatures: number;
}
